// =================================================================================
// File: src/components/Costplanner/CalculatorHelperPanel.jsx
// Description: Friendly, non-technical helper panel for the Advanced Calculator
// Explains the mental model, data sources, and reduces intimidation for first-time users
// =================================================================================

import React from "react";

/**
 * CalculatorHelperPanel
 * 
 * A calm, friendly guide that sits beside the product photo in the Advanced Calculator.
 * Explains what the calculator does, how to use it, and where its data comes from.
 * Pure presentational component with no side effects.
 * 
 * Future: Accept props for:
 * - Dynamic tips based on user progress
 * - Contextual hints based on missing data
 * - Links to actual setup pages (Materials, Utilities, Staff, etc.)
 * 
 * @param {Object} props - Future-ready for dynamic content
 * @param {string} [props.variant="default"] - Optional variant for different contexts
 * @param {boolean} [props.showDataSources=true] - Toggle data sources section visibility
 */
export default function CalculatorHelperPanel({ 
  variant = "default",
  showDataSources = true 
}) {
  return (
    <div className="rounded-lg border border-zinc-700/50 bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 p-5 backdrop-blur-sm space-y-5">
      
      {/* ========== SECTION A: How this calculator thinks ========== */}
      <div>
        {/* Visual anchor with subtle accent line */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center justify-center w-6 h-6 rounded bg-amber-500/10 border border-amber-500/20">
            <span className="text-amber-400/90 text-sm">🔥</span>
          </div>
          <h3 className="text-sm font-medium text-zinc-300 tracking-wide">
            How this calculator thinks
          </h3>
        </div>

        <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">
          <p className="text-zinc-200">
            This calculator helps you plan a piece — from clay to kiln.
          </p>
          
          <p>
            Start with the <span className="text-amber-400/90">finished object</span> — size, quantity, and clay type.
            From there, Mudo works backwards:
          </p>

          {/* Sequential steps with numbering */}
          <div className="ml-1 space-y-1.5 text-zinc-400 relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-amber-400/30 via-amber-400/10 to-transparent" />
            
            <div className="flex items-start gap-3 relative">
              <span className="text-[10px] font-mono text-amber-400/50 w-5 text-right shrink-0">01</span>
              <span>Clay needed</span>
            </div>
            <div className="flex items-start gap-3 relative">
              <span className="text-[10px] font-mono text-amber-400/50 w-5 text-right shrink-0">02</span>
              <span>Drying time</span>
            </div>
            <div className="flex items-start gap-3 relative">
              <span className="text-[10px] font-mono text-amber-400/50 w-5 text-right shrink-0">03</span>
              <span>Bisque & glaze firings</span>
            </div>
            <div className="flex items-start gap-3 relative">
              <span className="text-[10px] font-mono text-amber-400/50 w-5 text-right shrink-0">04</span>
              <span>Kiln space & load timing</span>
            </div>
          </div>

          <p className="text-zinc-400 italic pt-1">
            Nothing here is final — it's a planning tool, not a rulebook.
          </p>

          {/* Reassurance line with subtle emoji */}
          <div className="pt-2 border-t border-zinc-700/30">
            <p className="text-zinc-400 text-xs">
              Change values freely. You won't break anything <span className="opacity-70">🙂</span>
            </p>
          </div>
        </div>
      </div>

      {/* ========== SECTION B: Where the numbers come from ========== */}
      {showDataSources && (
        <div className="pt-4 border-t border-zinc-700/30">
          <h3 className="text-sm font-medium text-zinc-300 tracking-wide mb-3">
            Where the numbers come from
          </h3>

          <div className="space-y-2.5 text-xs leading-relaxed">
            <div className="flex items-start gap-2">
              <span className="text-blue-400/70 mt-0.5 font-mono text-[10px]">→</span>
              <div>
                <div className="text-zinc-200 font-semibold">Materials</div>
                <div className="text-zinc-500">Clay + glaze costs, shrinkage, wastage</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-blue-400/70 mt-0.5 font-mono text-[10px]">→</span>
              <div>
                <div className="text-zinc-200 font-semibold">Utilities</div>
                <div className="text-zinc-500">Firing cost estimates (power/gas + overhead)</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-blue-400/70 mt-0.5 font-mono text-[10px]">→</span>
              <div>
                <div className="text-zinc-200 font-semibold">Staff</div>
                <div className="text-zinc-500">Time + labour cost for each step</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-blue-400/70 mt-0.5 font-mono text-[10px]">→</span>
              <div>
                <div className="text-zinc-200 font-semibold">Kiln</div>
                <div className="text-zinc-500">Capacity, shelf height, load rules, duration</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-blue-400/70 mt-0.5 font-mono text-[10px]">→</span>
              <div>
                <div className="text-zinc-200 font-semibold">Packaging</div>
                <div className="text-zinc-500">Box types, wrap, labels, packing time/cost</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-blue-400/70 mt-0.5 font-mono text-[10px]">→</span>
              <div>
                <div className="text-zinc-200 font-semibold">Studio Settings</div>
                <div className="text-zinc-500">Tax, margin targets, currency, rounding</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== SECTION C: If something looks wrong ========== */}
      <div className="pt-4 border-t border-zinc-700/30">
        <h3 className="text-sm font-medium text-zinc-300 tracking-wide mb-3">
          If something feels off…
        </h3>

        <div className="space-y-2.5 text-xs text-zinc-400 leading-relaxed">
          <p className="text-zinc-300">
            It's usually because Materials, Utilities, or Staff aren't configured yet. The calculator uses sensible defaults in the meantime.
          </p>

          <p className="text-zinc-500">
            You can update them anytime:
          </p>

          <div className="ml-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-amber-400/50">•</span>
              <span className="text-zinc-400">
                Update in <span className="text-blue-400/80 hover:text-blue-400/100 cursor-pointer underline decoration-dotted">Materials →</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-amber-400/50">•</span>
              <span className="text-zinc-400">
                Update in <span className="text-blue-400/80 hover:text-blue-400/100 cursor-pointer underline decoration-dotted">Utilities →</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-amber-400/50">•</span>
              <span className="text-zinc-400">
                Update in <span className="text-blue-400/80 hover:text-blue-400/100 cursor-pointer underline decoration-dotted">Staff →</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-amber-400/50">•</span>
              <span className="text-zinc-400">
                Update in <span className="text-blue-400/80 hover:text-blue-400/100 cursor-pointer underline decoration-dotted">Kiln Settings →</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Mudo-signature tip at bottom */}
      <div className="pt-3 border-t border-zinc-700/30">
        <p className="text-xs text-zinc-500 italic">
          Tip: Measure the finished piece, not the hope <span className="opacity-70">😉</span>
        </p>
      </div>
    </div>
  );
}
